/*
 * ------------------------------------------------------------------------------------------
 * - External Imports -
 * ------------------------------------------------------------------------------------------
 */
import { OnlinePatientManagementProvider } from '@ngt/opms';

/*
 * ------------------------------------------------------------------------------------------
 * - Internal Imports -
 * ------------------------------------------------------------------------------------------
 */
import { Integration as SentryIntegration } from "@sentry/core";
import * as Sentry from '@sentry/react';
import { JsonServiceClient } from '@servicestack/client';
import * as React from 'react';
import { render } from 'react-dom';
import { GetFormMetadata, GetFormMetadataResponse, GetReportSettings, GetReportSettingsResponse, GetUser, GetUserResponse } from './api/dtos';
import App from './App';
import { AppSettings } from './AppSettings';
import RouteErrorBoundary from './components/error/RouteErrorBoundary';
import ReportSettingsContext from './contexts/ReportSettingsContext';
import createOnlinePatientManagement from './opms/OnlinePatientManagement';
import { createSentryAnonymousUser } from './utils/errors';

/*
 * ------------------------------------------------------------------------------------------
 * - Render -
 * ------------------------------------------------------------------------------------------
 */

declare let global: any;

if (typeof global === 'undefined') {
    (window as any).global = window;
}

if (AppSettings.sentry.dsn) {
    const integrations: SentryIntegration[] = [
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];

    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            }
        }));
    }

    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}


const startApp = (user?: GetUserResponse, formMetadata?: GetFormMetadataResponse, reportSettings?: GetReportSettingsResponse) => {
    const onlinePatientManagement = createOnlinePatientManagement(user, formMetadata, reportSettings);
    
    if(onlinePatientManagement?.history) {
        Sentry.addIntegration(Sentry.reactRouterV5BrowserTracingIntegration({ history: onlinePatientManagement.history }),)
    }

    if (!onlinePatientManagement) {
        return;
    }

    if (!reportSettings) {
        return;
    }

    render(
        <>
            <ReportSettingsContext.Provider value={reportSettings}>
                <OnlinePatientManagementProvider value={onlinePatientManagement}>
                    <Sentry.ErrorBoundary fallback={RouteErrorBoundary}>
                        <App />
                    </Sentry.ErrorBoundary>
                </OnlinePatientManagementProvider>
            </ReportSettingsContext.Provider>
        </>,
        document.getElementById('root')
    );
}

const bootstrap = async () => {
    if (AppSettings.debug) {
        const client = new JsonServiceClient();

        let user: GetUserResponse | undefined = undefined;

        try {
            user = await client.get(new GetUser());
        }
        catch (error) {
            user = undefined;
            console.error(error);
        }
        
        let formMetadata: GetFormMetadataResponse | undefined = undefined;

        try {
            formMetadata = await client.get(new GetFormMetadata());
        }
        catch (error) {
            formMetadata = undefined;
            console.error(error);
        }

        let reportSettings: GetReportSettingsResponse | undefined = undefined;

        try {
            reportSettings = await client.get(new GetReportSettings());
        }
        catch (error) {
            reportSettings = undefined;
            console.error(error);
        }
    
        if (reportSettings === undefined) {
            return "";
        }

        startApp(user, formMetadata, reportSettings);
    }
    else {
        const user = global.AUTH as GetUserResponse | undefined;
        const formMetadata = global.FORM_METADATA as GetFormMetadataResponse | undefined;
        const reportSettings = (global as any).REPORT_SETTINGS as GetReportSettingsResponse | undefined;

        startApp(user, formMetadata, reportSettings);
    }
}

bootstrap();